import React, { useCallback, useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { clearEmailVerificationSkip } from '../utils/emailVerificationSkip';
import {
  clearPendingAccountUpdate,
  savePendingAccountUpdate,
} from '../utils/accountUpdateVerification';
import { fetchWithSessionHandling } from '../utils/authFetch';
import { normalizeEmail } from '../utils/normalizeEmail';
import PasswordTextField from './PasswordTextField';

const API_URL = process.env.REACT_APP_API_URL;

type ViewerUser = {
  id: string;
  username: string;
  email: string;
  emailVerifiedAt?: string | null;
};

const UserSettings: React.FC = () => {
  const { user, token, setAuthFromTokenResponse, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [hospitalName, setHospitalName] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewerUsername, setViewerUsername] = useState('');
  const [viewerEmail, setViewerEmail] = useState('');
  const [viewerPassword, setViewerPassword] = useState('');
  const [viewerConfirmPassword, setViewerConfirmPassword] = useState('');
  const [viewerError, setViewerError] = useState('');
  const [viewerSuccess, setViewerSuccess] = useState('');
  const [isViewerSubmitting, setIsViewerSubmitting] = useState(false);
  const [viewerList, setViewerList] = useState<ViewerUser[]>([]);
  const [viewerListError, setViewerListError] = useState('');
  const [viewerListSuccess, setViewerListSuccess] = useState('');
  const [isViewerListLoading, setIsViewerListLoading] = useState(false);
  const [viewerResendingId, setViewerResendingId] = useState<string | null>(null);
  const [viewerDeleteTarget, setViewerDeleteTarget] = useState<ViewerUser | null>(null);
  const [isViewerDeleting, setIsViewerDeleting] = useState(false);
  const isAdmin = Boolean(user?.isAdmin);
  const isViewer = Boolean(user?.isViewer);
  const canManageViewers = !isAdmin && !user?.isExpired;

  useEffect(() => {
    const state = location.state as { accountUpdateVerified?: boolean } | undefined;
    if (state?.accountUpdateVerified) {
      clearPendingAccountUpdate();
      setSuccess('アカウント設定の変更を適用しました。');
      setError('');
      navigate('/user-settings', { replace: true });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    if (user) {
      setUsername(user.username ?? '');
      setHospitalName(user.hospitalName ?? '');
      setEmail(user.pendingEmail ?? user.email ?? '');
    }
  }, [user]);

  const fetchViewers = useCallback(async () => {
    if (!canManageViewers) {
      return;
    }

    const authToken = token;
    if (!authToken) {
      setViewerListError('認証情報が見つかりません。再度ログインしてください。');
      return;
    }

    setIsViewerListLoading(true);
    setViewerListError('');

    try {
      const { response, sessionExpired } = await fetchWithSessionHandling(
        `${API_URL}/api/auth/viewers`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
        { logout },
      );

      if (sessionExpired) {
        return;
      }

      if (!response.ok) {
        setViewerListError('閲覧専用ユーザーの一覧取得に失敗しました。時間をおいて再度お試しください。');
        return;
      }

      const data = await response.json();
      if (Array.isArray(data)) {
        setViewerList(data);
      } else {
        setViewerList([]);
      }
    } catch (err) {
      setViewerListError('閲覧専用ユーザーの一覧取得に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setIsViewerListLoading(false);
    }
  }, [canManageViewers, logout, token]);

  useEffect(() => {
    if (!canManageViewers) {
      setViewerList([]);
      setViewerListError('');
      setViewerListSuccess('');
      setIsViewerListLoading(false);
      return;
    }

    void fetchViewers();
  }, [fetchViewers, canManageViewers]);

  const validateForm = () => {
    if (!username || !email) {
      setError('ユーザー名とメールアドレスは必須です。');
      return false;
    }

    if (username.length > 15) {
      setError('ユーザー名は15文字以内で入力してください。');
      return false;
    }

    if (hospitalName && hospitalName.length > 20) {
      setError('病院名は20文字以内で入力してください。');
      return false;
    }

    const normalizedEmail = normalizeEmail(email);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(normalizedEmail)) {
      setError('有効なメールアドレスを入力してください。');
      return false;
    }

    const emailChanged = normalizedEmail !== normalizeEmail(user?.email ?? '');
    if (emailChanged && !currentPassword) {
      setError('メールアドレスを変更するには現在のパスワードを入力してください。');
      return false;
    }

    if (password) {
      if (!currentPassword) {
        setError('パスワードを変更するには現在のパスワードを入力してください。');
        return false;
      }

      if (password.length < 8) {
        setError('パスワードは8文字以上である必要があります。');
        return false;
      }

      const hasLetter = /[a-zA-Z]/.test(password);
      const hasNumber = /\d/.test(password);
      const hasSymbol = /[^A-Za-z0-9]/.test(password);
      const typeCount = [hasLetter, hasNumber, hasSymbol].filter(Boolean).length;
      if (typeCount < 2) {
        setError('パスワードは英字・数字・記号のうち、少なくとも2種類を含む必要があります。');
        return false;
      }

      if (password !== confirmPassword) {
        setError('パスワードと確認用パスワードが一致しません。');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) {
      return;
    }

    if (!token) {
      setError('認証情報が見つかりません。再度ログインしてください。');
      return;
    }

    setIsSubmitting(true);

    try {
      const normalizedEmail = normalizeEmail(email);
      const payload: Record<string, string | null> = {
        username,
        email: normalizedEmail,
        hospitalName: hospitalName ? hospitalName : null,
      };

      const emailChanged = normalizedEmail !== normalizeEmail(user?.email ?? '');
      if (password || emailChanged) {
        payload.currentPassword = currentPassword;
      }
      if (password) {
        payload.newPassword = password;
      }

      const response = await axios.put(`${API_URL}/api/auth/me`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const isSessionReady = await setAuthFromTokenResponse(response.data);
      if (!isSessionReady) {
        setError('セッション確認に失敗しました。再ログインが必要です。');
        return;
      }
      setCurrentPassword('');
      setPassword('');
      setConfirmPassword('');

      if (response.data.error === 'account_update_verification_required') {
        const responseUserId = response.data.user?.id || user?.id;
        let challenge = response.data.accountUpdateChallenge;

        if (!challenge?.id || !challenge?.targetEmail) {
          try {
            const challengeResponse = await axios.get(
              `${API_URL}/api/auth/account-update/challenge`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              },
            );
            challenge = challengeResponse.data;
          } catch {
            setSuccess('');
            setError('認証手続きの情報を確認できませんでした。再度お試しください。');
            return;
          }
        }

        if (!responseUserId || !challenge?.id || !challenge?.targetEmail) {
          setSuccess('');
          setError('認証手続きの情報を確認できませんでした。再度お試しください。');
          return;
        }

        savePendingAccountUpdate(responseUserId, challenge);
        const queryParams = new URLSearchParams({
          mode: 'accountUpdate',
          accountUpdate: 'true',
        });
        if (normalizedEmail) {
          queryParams.set('email', normalizedEmail);
        }
        setError('');
        setSuccess('認証コードを送信しました。メールに記載された手順に従って変更を完了してください。');
        navigate(`/verify?${queryParams.toString()}`, {
          state: { mode: 'accountUpdate', accountUpdate: true },
        });
      } else {
        setSuccess('変更内容はありませんでした。');
      }
    } catch (err: any) {
      if (err.response?.data?.detail === 'email_exists') {
        setError('このメールアドレスは既に使用されています。');
      } else if (err.response?.data?.detail === 'verification_recently_sent') {
        setError('認証コードはすでに送信されています。しばらく経ってから再度お試しください。');
      } else if (err.response?.data?.detail === 'failed_to_send_email') {
        setError('認証メールの送信に失敗しました。時間をおいて再度お試しください。');
      } else if (err.response?.data?.detail === 'current_password_required') {
        setError('パスワードを変更するには現在のパスワードを入力してください。');
      } else if (err.response?.data?.detail === 'current_password_invalid') {
        setError('現在のパスワードが正しくありません。');
      } else {
        setError('更新に失敗しました。時間をおいて再度お試しください。');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenDeleteDialog = () => {
    setError('');
    setSuccess('');
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    if (!isDeleting) {
      setIsDeleteDialogOpen(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!token) {
      setError('認証情報が見つかりません。再度ログインしてください。');
      return;
    }

    setError('');
    setIsDeleting(true);

    try {
      await axios.delete(`${API_URL}/api/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setIsDeleteDialogOpen(false);
      const emailToClear = user?.email || email;
      if (emailToClear) {
        clearEmailVerificationSkip(emailToClear);
      }
      navigate('/account-deleted', { replace: true });
    } catch (err: any) {
      if (err.response?.data?.detail === 'default_admin_cannot_be_deleted') {
        setError('このアカウントは削除できません。');
      } else if (err.response?.data?.detail === 'failed_to_delete_user') {
        setError('アカウントの削除処理に失敗しました。時間をおいて再度お試しください。');
      } else {
        setError('アカウントの削除に失敗しました。時間をおいて再度お試しください。');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const validateViewerForm = () => {
    if (!viewerUsername || !viewerEmail || !viewerPassword) {
      setViewerError('ユーザー名、メールアドレス、パスワードは必須です。');
      return false;
    }

    if (!viewerConfirmPassword) {
      setViewerError('確認用パスワードを入力してください。');
      return false;
    }

    if (viewerUsername.length > 15) {
      setViewerError('ユーザー名は15文字以内で入力してください。');
      return false;
    }

    const normalizedViewerEmail = normalizeEmail(viewerEmail);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(normalizedViewerEmail)) {
      setViewerError('有効なメールアドレスを入力してください。');
      return false;
    }

    if (viewerPassword.length < 8) {
      setViewerError('パスワードは8文字以上である必要があります。');
      return false;
    }

    const hasLetter = /[a-zA-Z]/.test(viewerPassword);
    const hasNumber = /\d/.test(viewerPassword);
    const hasSymbol = /[^A-Za-z0-9]/.test(viewerPassword);
    const typeCount = [hasLetter, hasNumber, hasSymbol].filter(Boolean).length;
    if (typeCount < 2) {
      setViewerError('パスワードは英字・数字・記号のうち、少なくとも2種類を含む必要があります。');
      return false;
    }

    if (viewerPassword !== viewerConfirmPassword) {
      setViewerError('パスワードと確認用パスワードが一致しません。');
      return false;
    }

    return true;
  };

  const handleViewerSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setViewerError('');
    setViewerSuccess('');

    if (!canManageViewers) {
      return;
    }

    if (!validateViewerForm()) {
      return;
    }

    const authToken = token;
    if (!authToken) {
      setViewerError('認証情報が見つかりません。再度ログインしてください。');
      return;
    }

    setIsViewerSubmitting(true);

    try {
      const { response, sessionExpired } = await fetchWithSessionHandling(
        `${API_URL}/api/auth/viewers`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            email: normalizeEmail(viewerEmail),
            password: viewerPassword,
            username: viewerUsername,
          }),
        },
        { logout },
      );

      if (sessionExpired) {
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        if (errorData?.detail === 'email_exists') {
          setViewerError('このメールアドレスは既に使用されています。');
        } else {
          setViewerError('閲覧専用ユーザーの作成に失敗しました。時間をおいて再度お試しください。');
        }
        return;
      }

      setViewerSuccess('認証メールを送信しました。本人確認完了後にログインできます。');
      setViewerUsername('');
      setViewerEmail('');
      setViewerPassword('');
      setViewerConfirmPassword('');
      await fetchViewers();
    } catch (err) {
      setViewerError('閲覧専用ユーザーの作成に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setIsViewerSubmitting(false);
    }
  };

  const handleResendViewerActivation = async (viewer: ViewerUser) => {
    if (!canManageViewers) {
      return;
    }

    const authToken = token;
    if (!authToken) {
      setViewerListError('認証情報が見つかりません。再度ログインしてください。');
      return;
    }

    setViewerResendingId(viewer.id);
    setViewerListError('');
    setViewerListSuccess('');

    try {
      const { response, sessionExpired } = await fetchWithSessionHandling(
        `${API_URL}/api/auth/viewers/${viewer.id}/resend-activation`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
        { logout },
      );

      if (sessionExpired) {
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        if (
          response.status === 429
          || errorData?.detail === 'verification_recently_sent'
        ) {
          setViewerListError('認証メールは60秒以上間隔を空けて再送してください。');
        } else if (errorData?.detail === 'viewer_already_activated') {
          setViewerListError('この閲覧専用ユーザーは本人確認済みです。');
        } else {
          setViewerListError('認証メールの再送に失敗しました。時間をおいて再度お試しください。');
        }
        return;
      }

      setViewerListSuccess('認証メールを再送しました。');
    } catch {
      setViewerListError('認証メールの再送に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setViewerResendingId(null);
    }
  };

  const handleOpenViewerDeleteDialog = (viewer: ViewerUser) => {
    setViewerListError('');
    setViewerListSuccess('');
    setViewerDeleteTarget(viewer);
  };

  const handleCloseViewerDeleteDialog = () => {
    if (!isViewerDeleting) {
      setViewerDeleteTarget(null);
    }
  };

  const handleConfirmViewerDelete = async () => {
    if (!canManageViewers) {
      return;
    }

    const authToken = token;
    if (!authToken) {
      setViewerListError('認証情報が見つかりません。再度ログインしてください。');
      return;
    }

    if (!viewerDeleteTarget) {
      return;
    }

    setIsViewerDeleting(true);
    setViewerListError('');
    setViewerListSuccess('');

    try {
      const { response, sessionExpired } = await fetchWithSessionHandling(
        `${API_URL}/api/auth/viewers/${viewerDeleteTarget.id}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
        { logout },
      );

      if (sessionExpired) {
        return;
      }

      if (!response.ok) {
        setViewerListError('閲覧専用ユーザーの削除に失敗しました。時間をおいて再度お試しください。');
        return;
      }

      setViewerListSuccess('閲覧専用ユーザーを削除しました。');
      setViewerDeleteTarget(null);
      await fetchViewers();
    } catch (err) {
      setViewerListError('閲覧専用ユーザーの削除に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setIsViewerDeleting(false);
    }
  };

  if (isViewer) {
    return <Navigate to="/" replace />;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" component="h1" align="center" gutterBottom>
          アカウント設定
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {user?.pendingEmail && user.pendingEmail !== user.email && (
          <Alert severity="info" sx={{ mb: 3 }}>
            新しいメールアドレス（{user.pendingEmail}）の認証が完了していません。メールをご確認のうえ、認証コードを入力してください。
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            margin="normal"
            required
            fullWidth
            id="username"
            label="ユーザー名"
            name="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={isSubmitting || isDeleting}
            inputProps={{ maxLength: 15 }}
          />

          <TextField
            margin="normal"
            fullWidth
            id="hospitalName"
            label="病院名または代表者様氏名（任意）"
            name="hospitalName"
            value={hospitalName}
            onChange={(e) => setHospitalName(e.target.value)}
            disabled={isSubmitting || isDeleting}
            inputProps={{ maxLength: 20 }}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="メールアドレス"
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isSubmitting || isDeleting}
          />

          <PasswordTextField
            margin="normal"
            fullWidth
            name="currentPassword"
            label="現在のパスワード"
            type="password"
            id="currentPassword"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            disabled={isSubmitting || isDeleting}
            helperText="パスワードを変更する場合のみ入力してください"
          />

          <PasswordTextField
            margin="normal"
            fullWidth
            name="password"
            label="新しいパスワード（任意）"
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isSubmitting || isDeleting}
            helperText="8文字以上で、英字・数字・記号のうち2種類以上を含む必要があります"
          />

          <PasswordTextField
            margin="normal"
            fullWidth
            name="confirmPassword"
            label="新しいパスワード（確認）"
            type="password"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={isSubmitting || !password || isDeleting}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 4, py: 1.5 }}
            disabled={isSubmitting || isDeleting}
          >
            {isSubmitting ? (
              <CircularProgress size={24} sx={{ color: 'white' }} />
            ) : (
              '更新する'
            )}
          </Button>

          <Button
            fullWidth
            variant="outlined"
            color="error"
            onClick={handleOpenDeleteDialog}
            disabled={isDeleting || isSubmitting}
            sx={{ py: 1.5 }}
          >
            アカウントを削除する
          </Button>
        </Box>
      </Paper>

      {canManageViewers && (
        <>
          <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
            <Typography variant="h6" component="h2" gutterBottom>
              閲覧専用ユーザーの作成
            </Typography>

            {viewerError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {viewerError}
              </Alert>
            )}

            {viewerSuccess && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {viewerSuccess}
              </Alert>
            )}

            <Box component="form" onSubmit={handleViewerSubmit} noValidate>
              <TextField
                margin="normal"
                fullWidth
                id="viewerHospitalName"
                label="病院名（表示のみ）"
                name="viewerHospitalName"
                value={user?.hospitalName || '未設定'}
                disabled
              />

              <TextField
                margin="normal"
                required
                fullWidth
                id="viewerUsername"
                label="ユーザー名"
                name="viewerUsername"
                value={viewerUsername}
                onChange={(e) => setViewerUsername(e.target.value)}
                disabled={isViewerSubmitting}
                inputProps={{ maxLength: 15 }}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                id="viewerEmail"
                label="メールアドレス"
                name="viewerEmail"
                type="email"
                value={viewerEmail}
                onChange={(e) => setViewerEmail(e.target.value)}
                disabled={isViewerSubmitting}
              />

              <PasswordTextField
                margin="normal"
                required
                fullWidth
                name="viewerPassword"
                label="パスワード"
                type="password"
                id="viewerPassword"
                autoComplete="new-password"
                value={viewerPassword}
                onChange={(e) => setViewerPassword(e.target.value)}
                disabled={isViewerSubmitting}
                helperText="8文字以上で、英字・数字・記号のうち2種類以上を含む必要があります"
              />

              <PasswordTextField
                margin="normal"
                required
                fullWidth
                name="viewerConfirmPassword"
                label="パスワード（確認）"
                type="password"
                id="viewerConfirmPassword"
                autoComplete="new-password"
                value={viewerConfirmPassword}
                onChange={(e) => setViewerConfirmPassword(e.target.value)}
                disabled={isViewerSubmitting}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, py: 1.5 }}
                disabled={isViewerSubmitting}
              >
                {isViewerSubmitting ? (
                  <CircularProgress size={24} sx={{ color: 'white' }} />
                ) : (
                  '閲覧専用ユーザーを作成する'
                )}
              </Button>
            </Box>
          </Paper>

          <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
            <Typography variant="h6" component="h2" gutterBottom>
              閲覧専用ユーザー一覧
            </Typography>

            {viewerListError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {viewerListError}
              </Alert>
            )}

            {viewerListSuccess && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {viewerListSuccess}
              </Alert>
            )}

            {isViewerListLoading ? (
              <Box display="flex" justifyContent="center" sx={{ py: 3 }}>
                <CircularProgress />
              </Box>
            ) : viewerList.length === 0 ? (
              <Typography color="text.secondary">閲覧専用ユーザーは登録されていません。</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ユーザー名</TableCell>
                      <TableCell>メールアドレス</TableCell>
                      <TableCell>本人確認</TableCell>
                      <TableCell align="left">操作</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {viewerList.map((viewer) => (
                      <TableRow key={viewer.id}>
                        <TableCell>{viewer.username}</TableCell>
                        <TableCell>{viewer.email}</TableCell>
                        <TableCell>
                          {viewer.emailVerifiedAt ? '確認済み' : '確認待ち'}
                        </TableCell>
                        <TableCell align="right">
                          {!viewer.emailVerifiedAt && (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleResendViewerActivation(viewer)}
                              disabled={
                                isViewerDeleting
                                || viewerResendingId === viewer.id
                              }
                              sx={{ mr: 1 }}
                            >
                              {viewerResendingId === viewer.id
                                ? '再送中...'
                                : '認証メールを再送'}
                            </Button>
                          )}
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => handleOpenViewerDeleteDialog(viewer)}
                            disabled={isViewerDeleting || viewerResendingId !== null}
                          >
                            削除
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}

      <Dialog open={isDeleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>アカウント削除の確認</DialogTitle>
        <DialogContent>
          <DialogContentText>
            アカウントを削除すると、アップロードしたデータを含む全ての情報は元に戻せません。また、Stripeのサブスクリプションも即時に解約され、残りの利用期間に関わらず返金は行われません。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={isDeleting}>
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : '削除する'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(viewerDeleteTarget)}
        onClose={handleCloseViewerDeleteDialog}
      >
        <DialogTitle>閲覧専用ユーザー削除の確認</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {viewerDeleteTarget
              ? `閲覧専用ユーザー「${viewerDeleteTarget.username}」を削除します。よろしいですか？`
              : '閲覧専用ユーザーを削除します。よろしいですか？'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewerDeleteDialog} disabled={isViewerDeleting}>
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmViewerDelete}
            color="error"
            variant="contained"
            disabled={isViewerDeleting}
          >
            {isViewerDeleting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : '削除する'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserSettings;
